import { Gallery } from 'iconsax-react';
import { isNotNil } from 'rambda';
import { useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import GroupDefaultAvatarIcon from '@/assets/images/contact/group-default-avatar.svg?react';
import type { UserCarouselItem } from '@/components/common/user-carousel/user-carousel';
import { UploadImageField } from '@/components/fields/upload-image-field';
import useFriends from '@/hooks/contacts/use-friends';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import useUserInfo from '@/hooks/users/use-user-info';
import { uploadableScheme } from '@/models/uploadable';
import { useAuthStore } from '@/stores/auth';
import cn from '@/utils/merge-class-names';
import FormInput from '../common/form-input';
import SelectContactList from './contact-list/select-contact-list';

type Props = {
  step: number;
  form: UseFormReturn<CreateGroupFormData>;
};

export function CreateGroupForm({ step, form }: Props) {
  const { control, watch } = form;
  const selfId = useAuthStore((s) => s.auth?.UserID);

  const { friends } = useFriends();
  const memberIds = watch('UserIDs', []);

  const { data: userInfo } = useUserInfo();
  const { getFileSizeLimit } = useFileSizeLimit();
  const [isGroupNameEdited, setIsGroupNameEdited] = useState(false);

  const headImageSizeLimit = useMemo(() => getFileSizeLimit('image'), [getFileSizeLimit]);

  const selectedFriends = useMemo(
    () =>
      memberIds
        .map((userId) => {
          const member = friends[userId];

          return member
            ? ({
                userId: member.FriendID,
                avatarUrl: member.FriendHeadImg,
                label: member.RemarkName || member.FriendNickName,
              } satisfies UserCarouselItem)
            : undefined;
        })
        .filter(isNotNil),
    [memberIds, friends],
  );

  useEffect(() => {
    if (!step) {
      form.resetField('GroupName', { defaultValue: '' });
      setIsGroupNameEdited(false);
      return;
    }

    if (!isGroupNameEdited) {
      setDefaultGroupName();
    }
  }, [friends, selectedFriends, step, userInfo?.NickName]);

  const setDefaultGroupName = () => {
    const [firstMember] = selectedFriends;
    const friend = friends[firstMember?.userId || -1];
    const friendName = friend?.RemarkName || friend?.FriendNickName || '';
    const groupName = `${userInfo?.NickName || ''}、${friendName} ...` as const;
    form.setValue('GroupName', groupName);
  };

  const handleGroupNameChange = (value: string) => {
    setIsGroupNameEdited(value.length > 0);
    form.setValue('GroupName', value);
  };

  return (
    <form className='flex w-full flex-1 flex-col gap-4'>
      <div className={cn('flex w-full px-5', !step && 'hidden')}>
        <div className='relative mr-6 size-20'>
          <UploadImageField
            name='GroupImg'
            control={control}
            className='size-20'
            limitSize={headImageSizeLimit}
            fallback={
              <div className='relative size-20'>
                <div className='bg-primary rounded-full flex flex-center size-20 p-5'>
                  <GroupDefaultAvatarIcon className='size-10' />
                </div>

                <div className='btn btn-circle btn-xs absolute bottom-0 right-0 bg-neutral-0 shadow-xl'>
                  <Gallery className='size-3 text-secondary' />
                </div>
              </div>
            }>
            {(fieldValue, preview) => (
              <div className='inline-flex'>
                <img
                  src={preview || fieldValue?.url || ''}
                  alt='group avatar'
                  className='rounded-full size-16 aspect-square object-center object-cover'
                  width={64}
                  height={64}
                  fetchPriority='low'
                />

                <div className='btn btn-circle btn-xs absolute bottom-0 right-0 bg-neutral-0 shadow-xl'>
                  <Gallery className='size-3 text-secondary' />
                </div>
              </div>
            )}
          </UploadImageField>
        </div>

        <label
          htmlFor='GroupName'
          className='flex flex-1 items-center gap-8'>
          <FormInput
            control={control}
            name='GroupName'
            className='w-full'
            onChange={(e) => handleGroupNameChange(e.target.value)}
          />
        </label>
      </div>
      <div className='flex-1 flex flex-col'>
        <Controller
          control={control}
          name='UserIDs'
          render={({ field }) => (
            <SelectContactList
              selectedFriendIds={[...(step && selfId ? [selfId] : []), ...field.value]}
              onSelectFriends={(ids) => {
                field.onChange(ids);
              }}
              isContactListHidden={!!step}
              disableRemove={!!step && field.value.length === 1}
            />
          )}
        />
      </div>
    </form>
  );
}

export const createGroupFormScheme = z.object({
  GroupName: z.string().trim().min(1, 'errors.string.min-length|count=1').max(50, 'errors.string.max-length|count=50'),
  GroupImg: uploadableScheme.optional(),
  UserIDs: z.array(z.number()).min(1),
});

export type CreateGroupFormData = z.infer<typeof createGroupFormScheme>;
